import React, { useState, useEffect, useCallback } from 'react';
import { singularAPI } from '../../lib/api';

const LS_KEY = (showId) => `singular_preview_${showId}`;

function loadSaved(showId) {
  try {
    const raw = localStorage.getItem(LS_KEY(showId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSaved(showId, data) {
  try {
    localStorage.setItem(LS_KEY(showId), JSON.stringify(data));
  } catch {}
}

// 16:9 iframe panel
function OutputPanel({ label, url, urlDraft, onUrlDraftChange, onUrlCommit }) {
  return (
    <div className="flex flex-col gap-2 flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{label}</span>
        {url && (
          <span className="text-[10px] text-green-500 bg-green-900/20 px-1.5 py-0.5 rounded">LIVE</span>
        )}
      </div>

      <div className="relative w-full bg-app-sub border border-app-border rounded overflow-hidden" style={{ paddingBottom: '56.25%' }}>
        {url ? (
          <iframe
            src={url}
            title={`${label} output`}
            allow="autoplay"
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
            <svg className="w-8 h-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 text-xs">Paste the {label.toLowerCase()} output URL below and press Enter</p>
          </div>
        )}
      </div>

      <input
        type="text"
        value={urlDraft}
        onChange={e => onUrlDraftChange(e.target.value)}
        onBlur={onUrlCommit}
        onKeyDown={e => { if (e.key === 'Enter') { onUrlCommit(); e.target.blur(); } e.stopPropagation(); }}
        placeholder={`Paste ${label} output URL…`}
        className="w-full bg-app-sub border border-app-border rounded px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-700 outline-none focus:border-app-border-strong font-mono"
      />
    </div>
  );
}

export default function SingularView({ showId }) {
  const saved = loadSaved(showId);

  const [previewUrl, setPreviewUrl] = useState(saved.previewUrl || '');
  const [liveUrl, setLiveUrl] = useState(saved.liveUrl || '');
  const [previewToken, setPreviewToken] = useState(saved.previewToken || '');

  const [previewUrlDraft, setPreviewUrlDraft] = useState(saved.previewUrl || '');
  const [liveUrlDraft, setLiveUrlDraft] = useState(saved.liveUrl || '');
  const [tokenDraft, setTokenDraft] = useState(saved.previewToken || '');

  const [compositions, setCompositions] = useState([]);
  const [loadingComps, setLoadingComps] = useState(false);
  const [compsError, setCompsError] = useState('');
  const [firingId, setFiringId] = useState(null); // id of in-flight call
  const [activeComps, setActiveComps] = useState(new Set()); // ids currently In

  function persist(updates) {
    const next = { previewUrl, liveUrl, previewToken, ...updates };
    saveSaved(showId, next);
  }

  function commitPreviewUrl() {
    const trimmed = previewUrlDraft.trim();
    setPreviewUrl(trimmed);
    persist({ previewUrl: trimmed });
  }

  function commitLiveUrl() {
    const trimmed = liveUrlDraft.trim();
    setLiveUrl(trimmed);
    persist({ liveUrl: trimmed });
  }

  function commitToken() {
    const trimmed = tokenDraft.trim();
    setPreviewToken(trimmed);
    persist({ previewToken: trimmed });
  }

  const loadCompositions = useCallback(async (token) => {
    if (!token) return;
    setLoadingComps(true);
    setCompsError('');
    try {
      const res = await singularAPI.previewCompositions(token);
      const comps = Array.isArray(res.data) ? res.data : [];
      setCompositions(comps);
      if (comps.length === 0) setCompsError('No compositions found for this token.');
    } catch (err) {
      setCompsError(err?.response?.data?.error || 'Failed to load compositions. Check the token.');
      setCompositions([]);
    } finally {
      setLoadingComps(false);
    }
  }, []);

  // Auto-load if token already saved
  useEffect(() => {
    if (saved.previewToken) {
      loadCompositions(saved.previewToken);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleToggleComposition(comp) {
    const id = comp.subCompositionId || comp.id;
    if (!id || !previewToken || firingId) return;
    const isActive = activeComps.has(id);
    setFiringId(id);
    try {
      if (isActive) {
        await singularAPI.previewHide(previewToken, id);
        setActiveComps(prev => { const n = new Set(prev); n.delete(id); return n; });
      } else {
        await singularAPI.previewShow(previewToken, id);
        setActiveComps(prev => new Set([...prev, id]));
      }
    } catch (err) {
      console.error('Failed to toggle preview composition:', err);
    } finally {
      setFiringId(null);
    }
  }

  function handleTokenKeyDown(e) {
    if (e.key === 'Enter') {
      commitToken();
      loadCompositions(tokenDraft.trim());
      e.target.blur();
    }
    e.stopPropagation();
  }

  const compName = (comp) => comp.mainComposition ? 'Overlay Customisation' : (comp.subCompositionName || comp.name || comp.subCompositionId || comp.id);

  return (
    <div className="flex flex-col h-full bg-app-bg overflow-y-auto">
      {/* Iframe panels */}
      <div className="flex gap-4 p-4">
        <OutputPanel
          label="Preview"
          url={previewUrl}
          urlDraft={previewUrlDraft}
          onUrlDraftChange={setPreviewUrlDraft}
          onUrlCommit={commitPreviewUrl}
        />
        <OutputPanel
          label="Live"
          url={liveUrl}
          urlDraft={liveUrlDraft}
          onUrlDraftChange={setLiveUrlDraft}
          onUrlCommit={commitLiveUrl}
        />
      </div>

      {/* Preview token + GFX list */}
      <div className="px-4 pb-6">
        <div className="border-t border-app-surface pt-4">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Preview GFX Control</p>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-wider text-gray-600 mb-1">Preview Token</label>
              <input
                type="text"
                value={tokenDraft}
                onChange={e => setTokenDraft(e.target.value)}
                onBlur={() => { commitToken(); }}
                onKeyDown={handleTokenKeyDown}
                placeholder="Paste Singular control app token…"
                className="w-full bg-app-sub border border-app-border rounded px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-700 outline-none focus:border-app-border-strong font-mono"
              />
            </div>
            <button
              onClick={() => { commitToken(); loadCompositions(tokenDraft.trim()); }}
              disabled={!tokenDraft.trim() || loadingComps}
              className="mt-4 px-3 py-1.5 text-xs font-semibold bg-app-surface hover:bg-app-border disabled:opacity-40 disabled:cursor-not-allowed text-gray-300 rounded border border-app-border-strong transition-colors flex-shrink-0"
            >
              {loadingComps ? 'Loading…' : 'Load GFX List'}
            </button>
          </div>

          {compsError && (
            <p className="text-red-400 text-xs mb-3">{compsError}</p>
          )}

          {compositions.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-2">
                Click to fire in preview — {compositions.length} composition{compositions.length !== 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {compositions.map(comp => {
                  const id = comp.subCompositionId || comp.id;
                  const isFiring = firingId === id;
                  const isActive = activeComps.has(id);
                  return (
                    <button
                      key={id}
                      onClick={() => handleToggleComposition(comp)}
                      disabled={!!firingId}
                      className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                        isFiring
                          ? 'bg-amber-700 border-amber-600 text-white'
                          : isActive
                          ? 'bg-purple-800 border-purple-600 text-white hover:bg-purple-700'
                          : 'bg-app-header hover:bg-app-surface border-app-border hover:border-app-border-strong text-gray-300'
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      {isFiring ? '…' : compName(comp)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!previewToken && compositions.length === 0 && !compsError && (
            <p className="text-gray-700 text-xs">Enter your Singular preview control app token above and click Load GFX List to see your compositions here. Clicking a composition will fire it on the preview output.</p>
          )}
        </div>
      </div>
    </div>
  );
}
